import React, { useState, useEffect, useCallback } from 'react';
import { getInvoices, payInvoice, cancelInvoice, getUsers } from '../../api/adminApi';
import LoadingData from '../../components/LoadingData';
import { useToast } from '../../context/ToastContext';
import { theme } from '../../adminTheme';

export default function InvoicesPage() {
  const toast = useToast();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [payModal, setPayModal] = useState(null);
  const [cancelModal, setCancelModal] = useState(null);
  const [detailsModal, setDetailsModal] = useState(null);

  // Fetch users for filter dropdown
  useEffect(() => {
    getUsers().then(res => setUsers(res.data.users || [])).catch(() => {});
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await getInvoices(page, 20, filterStatus || null, filterUserId || null);
      setInvoices(res.data.invoices);
      setTotal(res.data.total);
      setHasNext(res.data.has_next);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [page, filterStatus, filterUserId]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handlePay = async (e) => {
    e.preventDefault();
    try {
      await payInvoice(payModal.id, payModal.paymentMethod, payModal.transactionId, payModal.notes);
      toast.addToast('Factura pagada exitosamente', 'success');
      setPayModal(null);
      fetchInvoices();
    } catch (err) {
      toast.addToast(err.response?.data?.detail || 'Error al pagar factura', 'error');
    }
  };

  const handleCancel = async () => {
    if (!cancelModal || !cancelModal.reason?.trim()) {
      toast.addToast('Debes ingresar un motivo de cancelación', 'error');
      return;
    }
    try {
      await cancelInvoice(cancelModal.id, cancelModal.reason);
      toast.addToast('Factura cancelada exitosamente', 'success');
      setCancelModal(null);
      fetchInvoices();
    } catch (err) {
      toast.addToast(err.response?.data?.detail || 'Error al cancelar factura', 'error');
    }
  };

  if (loading) return <LoadingData />;

  const statusStyles = {
    paid: { bg: theme.successBg, color: theme.success },
    pending: { bg: theme.warningBg, color: theme.warning },
    cancelled: { bg: theme.errorBg, color: theme.error },
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Facturas</h1>

      <div style={styles.filters}>
        <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }} style={styles.select}>
          <option value="">Todas</option>
          <option value="pending">Pendientes</option>
          <option value="paid">Pagadas</option>
          <option value="cancelled">Canceladas</option>
        </select>
        <select value={filterUserId} onChange={(e) => { setFilterUserId(e.target.value); setPage(1); }} style={styles.select}>
          <option value="">Todos los usuarios</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.username} ({u.email})</option>
          ))}
        </select>
        <span style={{ color: theme.textMuted, fontSize: '0.9rem' }}>Total: {total} facturas</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}># Factura</th>
              <th style={styles.th}>Usuario</th>
              <th style={styles.th}>Bot</th>
              <th style={styles.th}>Período</th>
              <th style={styles.th}>Monto</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: 24, color: theme.textMuted }}>No hay facturas</td></tr>
            )}
            {invoices.map((inv, idx) => {
              const s = statusStyles[inv.status] || { bg: theme.card, color: theme.text };
              return (
                <tr key={inv.id} style={{ backgroundColor: idx % 2 === 0 ? theme.tableRow : theme.tableRowAlt }}>
                  <td style={styles.td}>{inv.invoice_number}</td>
                  <td style={styles.td}>{inv.user?.username || inv.user_id?.slice(-6) || '-'}</td>
                  <td style={styles.td}>{inv.bot_name}</td>
                  <td style={styles.td}>{inv.period}</td>
                  <td style={{ ...styles.td, fontWeight: 600, color: (inv.fee_amount || 0) > 0 ? theme.success : theme.text }}>${(inv.fee_amount || 0).toFixed(2)}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, backgroundColor: s.bg, color: s.color }}>
                      {inv.status === 'paid' ? 'Pagada' : inv.status === 'pending' ? 'Pendiente' : 'Cancelada'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {inv.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setPayModal({ id: inv.id, paymentMethod: '', transactionId: '', notes: '' })} style={styles.payBtn}>
                          Pagar
                        </button>
                        <button onClick={() => setCancelModal({ id: inv.id, reason: '' })} style={styles.cancelIconBtn}>
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => setDetailsModal(inv)} style={styles.detailsBtn}>
                        Detalles
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={styles.pagination}>
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.4 : 1 }}>Anterior</button>
        <span style={{ margin: '0 12px', color: theme.textMuted }}>Página {page}</span>
        <button disabled={!hasNext} onClick={() => setPage(p => p + 1)} style={{ ...styles.pageBtn, opacity: !hasNext ? 0.4 : 1 }}>Siguiente</button>
      </div>

      {cancelModal && (
        <div style={styles.modalOverlay} onClick={() => setCancelModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: theme.text, marginTop: 0 }}>Cancelar Factura</h3>
            <div style={styles.field}>
              <label style={styles.label}>Motivo de cancelación *</label>
              <textarea
                value={cancelModal.reason}
                onChange={(e) => setCancelModal({...cancelModal, reason: e.target.value})}
                style={styles.textarea}
                placeholder="Ingresa el motivo de la cancelación"
                required
              />
            </div>
            <div style={styles.modalActions}>
              <button onClick={handleCancel} style={styles.submitBtn}>Cancelar factura</button>
              <button onClick={() => setCancelModal(null)} style={styles.cancelBtn}>Volver</button>
            </div>
          </div>
        </div>
      )}

      {payModal && (
        <div style={styles.modalOverlay} onClick={() => setPayModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: theme.text, marginTop: 0 }}>Pagar Factura</h2>
            <form onSubmit={handlePay}>
              <div style={styles.field}>
                <label style={styles.label}>Método de pago</label>
                <input value={payModal.paymentMethod} onChange={(e) => setPayModal({...payModal, paymentMethod: e.target.value})} required style={styles.input} placeholder="Transferencia, efectivo, etc." />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Transaction ID (opcional)</label>
                <input value={payModal.transactionId} onChange={(e) => setPayModal({...payModal, transactionId: e.target.value})} style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Notas (opcional)</label>
                <textarea value={payModal.notes} onChange={(e) => setPayModal({...payModal, notes: e.target.value})} style={styles.textarea} />
              </div>
              <div style={styles.modalActions}>
                <button type="submit" style={styles.submitBtn}>Pagar</button>
                <button type="button" onClick={() => setPayModal(null)} style={styles.cancelBtn}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detailsModal && (
        <div style={styles.modalOverlay} onClick={() => setDetailsModal(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: theme.text, marginTop: 0, marginBottom: 16 }}>
              {detailsModal.status === 'paid' ? 'Detalles de Pago' : 'Detalles de Cancelación'}
            </h2>
            {detailsModal.status === 'paid' ? (
              <div style={styles.detailsGrid}>
                <DetailRow label="Estado" value="Pagada" />
                <DetailRow label="Pagado por" value={detailsModal.paid_by || '-'} />
                <DetailRow label="Método de pago" value={detailsModal.payment_method || '-'} />
                <DetailRow label="Transaction ID" value={detailsModal.transaction_id || '-'} />
                <DetailRow label="Notas" value={detailsModal.payment_notes || '-'} />
                <DetailRow label="Fecha de pago" value={detailsModal.paid_at ? new Date(detailsModal.paid_at).toLocaleString() : '-'} />
              </div>
            ) : (
              <div style={styles.detailsGrid}>
                <DetailRow label="Estado" value="Cancelada" />
                <DetailRow label="Cancelado por" value={detailsModal.cancelled_by || '-'} />
                <DetailRow label="Motivo" value={detailsModal.cancel_reason || '-'} />
                <DetailRow label="Fecha de cancelación" value={detailsModal.cancelled_at ? new Date(detailsModal.cancelled_at).toLocaleString() : '-'} />
              </div>
            )}
            <div style={styles.modalActions}>
              <button onClick={() => setDetailsModal(null)} style={styles.submitBtn}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={styles.detailRow}>
      <span style={styles.detailLabel}>{label}</span>
      <span style={styles.detailValue}>{value}</span>
    </div>
  );
}

const styles = {
  container: { padding: '24px', minHeight: '100vh' },
  title: { margin: '0 0 24px 0', fontSize: '1.5rem', color: theme.text },
  filters: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  select: { padding: '8px 12px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '0.9rem', backgroundColor: theme.input, color: theme.text },
  table: { width: '100%', borderCollapse: 'collapse', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${theme.border}` },
  th: { textAlign: 'left', padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, fontSize: '0.85rem', color: theme.textMuted, fontWeight: 600, backgroundColor: theme.tableHeader },
  td: { padding: '12px 16px', borderBottom: `1px solid ${theme.border}`, fontSize: '0.9rem', color: theme.text },
  statusBadge: { padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 500 },
  payBtn: { padding: '6px 12px', backgroundColor: theme.accent, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' },
  cancelIconBtn: { padding: '6px 12px', backgroundColor: 'transparent', border: `1px solid ${theme.error}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', color: theme.error },
  detailsBtn: { padding: '6px 12px', backgroundColor: 'transparent', border: `1px solid ${theme.accent}`, borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', color: theme.accent },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 20 },
  pageBtn: { padding: '8px 16px', border: `1px solid ${theme.border}`, borderRadius: '6px', backgroundColor: theme.card, color: theme.text, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: theme.card, padding: '24px', borderRadius: '12px', border: `1px solid ${theme.border}`, width: '90%', maxWidth: '420px' },
  field: { marginBottom: '12px' },
  label: { color: theme.textMuted, display: 'block', marginBottom: 4, fontSize: '0.9rem' },
  input: { width: '100%', padding: '8px 12px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '1rem', backgroundColor: theme.input, color: theme.text, boxSizing: 'border-box', marginTop: 4 },
  textarea: { width: '100%', padding: '8px 12px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '1rem', backgroundColor: theme.input, color: theme.text, boxSizing: 'border-box', marginTop: 4, minHeight: 60, fontFamily: 'inherit' },
  modalActions: { display: 'flex', gap: 8, marginTop: 16 },
  submitBtn: { flex: 1, padding: '10px', backgroundColor: theme.accent, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  cancelBtn: { flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.border}`, borderRadius: '6px', cursor: 'pointer', color: theme.text },
  detailsGrid: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8 },
  detailRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${theme.border}` },
  detailLabel: { color: theme.textMuted, fontSize: '0.85rem' },
  detailValue: { color: theme.text, fontSize: '0.9rem', fontWeight: 500, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' },
};
